import { useCallback, useEffect, useRef, useState } from "react";
import socket from "../../socket/socket";
import { trackDriver, trackDrivers } from "../../services/orderTrackService";

const DEBOUNCE_MS = 300;
const RECONCILE_MS = 30000;
const ANIMATION_MS = 450;

export const routeSignature = (route) => {
  const line = route?.polyline;
  if (!Array.isArray(line) || line.length === 0) return "";
  const first = line[0];
  const last = line[line.length - 1];
  return `${line.length}|${first?.lat},${first?.lng}|${last?.lat},${last?.lng}`;
};

const hasOpenOrders = (driver) =>
  Array.isArray(driver?.orders) && driver.orders.length > 0;

const withSignature = (driver) => ({
  ...driver,
  routeSignature: routeSignature(driver?.route),
});

const useDrivers = (branchSwitch, selectedDriver, setSelectedDriver) => {
  const [drivers, setDrivers] = useState([]);
  const [followTargetId, setFollowTargetId] = useState(null);

  const lastPositionsRef = useRef({});
  const framesRef = useRef({});
  const idsRef = useRef(new Set());
  const driversRef = useRef([]);
  const requestIdRef = useRef(0);
  const inFlightRef = useRef({});
  const queuedRef = useRef({});
  const mountedRef = useRef(true);

  const selectedRef = useRef(selectedDriver);
  selectedRef.current = selectedDriver;
  const branchRef = useRef(branchSwitch);
  branchRef.current = branchSwitch;

  const branchKey = branchSwitch?._id ?? "default";

  useEffect(() => {
    driversRef.current = drivers;
    idsRef.current = new Set(drivers.map((d) => d.driverId));
  }, [drivers]);

  const fetchDrivers = async () => {
    const requestId = ++requestIdRef.current;
    try {
      const res = await trackDrivers(branchRef.current);
      if (!mountedRef.current) return;
      if (requestId !== requestIdRef.current) return;
      if (res?.data?.code !== 1) return;

      const raw = Array.isArray(res.data.data) ? res.data.data : [];
      const next = raw.map(withSignature);

      next.forEach((d) => {
        if (d.location) lastPositionsRef.current[d.driverId] = d.location;
      });
      Object.keys(lastPositionsRef.current).forEach((id) => {
        if (!next.some((d) => d.driverId === id)) {
          delete lastPositionsRef.current[id];
          cancelAnimationFrame(framesRef.current[id]);
          delete framesRef.current[id];
        }
      });

      setDrivers(next);
    } catch (err) {
      console.error("Fetch Drivers Error:", err);
    }
  };
  const fetchDriversRef = useRef(fetchDrivers);
  fetchDriversRef.current = fetchDrivers;

  const fetchSingleDriver = async (driverId) => {
    if (!driverId) return;
    if (inFlightRef.current[driverId]) {
      queuedRef.current[driverId] = true;
      return;
    }
    inFlightRef.current[driverId] = true;

    try {
      const res = await trackDriver(branchRef.current, driverId);
      if (!mountedRef.current) return;
      const full = res?.data?.data;

      if (!full || !hasOpenOrders(full)) {
        delete lastPositionsRef.current[driverId];
        cancelAnimationFrame(framesRef.current[driverId]);
        delete framesRef.current[driverId];
        setDrivers((prev) => prev.filter((d) => d.driverId !== driverId));
        return;
      }

      if (full.location) lastPositionsRef.current[driverId] = full.location;

      setDrivers((prev) => {
        const exists = prev.some((d) => d.driverId === driverId);
        if (!exists) return [...prev, withSignature(full)];
        return prev.map((d) => {
          if (d.driverId !== driverId) return d;
          const merged = { ...d, ...full };
          if (!full.route && d.route) merged.route = d.route;
          if (!full.progress && d.progress) merged.progress = d.progress;
          merged.location = full.location ?? d.location;
          return withSignature(merged);
        });
      });
    } catch (err) {
      console.error("Single Driver Fetch Failed:", err);
    } finally {
      inFlightRef.current[driverId] = false;
      if (queuedRef.current[driverId]) {
        queuedRef.current[driverId] = false;
        fetchSingleRef.current(driverId);
      }
    }
  };
  const fetchSingleRef = useRef(fetchSingleDriver);
  fetchSingleRef.current = fetchSingleDriver;

  const updateSelectedDriver = async (driverId, follow = false) => {
    setFollowTargetId(follow ? driverId : null);

    const known = driversRef.current.find((d) => d.driverId === driverId);
    if (known) setSelectedDriver(known);

    try {
      const res = await trackDriver(branchRef.current, driverId);
      if (!mountedRef.current) return;
      const full = res?.data?.data;
      if (!full) return;

      setDrivers((prev) =>
        prev.map((d) => {
          if (d.driverId !== driverId) return d;
          const merged = { ...d, ...full };
          if (!full.route && d.route) merged.route = d.route;
          if (!full.progress && d.progress) merged.progress = d.progress;
          merged.location = lastPositionsRef.current[driverId] ?? d.location;
          return withSignature(merged);
        })
      );

      if (!known) {
        setSelectedDriver(
          withSignature({
            ...full,
            location: lastPositionsRef.current[driverId] ?? full.location,
          })
        );
      }
    } catch (err) {
      console.warn("Driver refresh failed, using cached data.");
    }
  };

  const animateTo = (driverId, target) => {
    const from = lastPositionsRef.current[driverId] ?? target;
    lastPositionsRef.current[driverId] = target;

    cancelAnimationFrame(framesRef.current[driverId]);
    const start = performance.now();

    const step = (ts) => {
      if (!mountedRef.current) return;
      const t = Math.min((ts - start) / ANIMATION_MS, 1);
      const position = {
        lat: from.lat + (target.lat - from.lat) * t,
        long: from.long + (target.long - from.long) * t,
      };
      setDrivers((prev) =>
        prev.map((d) =>
          d.driverId === driverId
            ? { ...d, prevLocation: from, location: position }
            : d
        )
      );
      if (t < 1) framesRef.current[driverId] = requestAnimationFrame(step);
    };

    framesRef.current[driverId] = requestAnimationFrame(step);
  };

  useEffect(() => {
    const handleLocation = (data) => {
      const driverId = data?.driverId;
      const lat = Number(data?.location?.lat);
      const long = Number(data?.location?.long);
      if (!driverId || !Number.isFinite(lat) || !Number.isFinite(long)) return;

      if (!idsRef.current.has(driverId)) {
        fetchSingleRef.current(driverId);
        return;
      }

      if (data.route || data.progress) {
        setDrivers((prev) =>
          prev.map((d) => {
            if (d.driverId !== driverId) return d;
            const next = { ...d };
            if (data.route) {
              next.route = data.route;
              next.routeSignature = routeSignature(data.route);
            }
            if (data.progress) next.progress = data.progress;
            return next;
          })
        );
      }

      animateTo(driverId, { lat, long });
    };

    socket.on("admin:driverLocationUpdate", handleLocation);
    return () => socket.off("admin:driverLocationUpdate", handleLocation);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let timer = null;
    const pendingDrivers = new Set();
    let pendingFull = false;

    const flush = () => {
      const ids = Array.from(pendingDrivers);
      const full = pendingFull;
      pendingDrivers.clear();
      pendingFull = false;
      if (full || ids.length > 3) fetchDriversRef.current();
      else ids.forEach((id) => fetchSingleRef.current(id));
    };

    const handleOrderUpdate = (data) => {
      if (!data) return;
      if (data.driverId) pendingDrivers.add(data.driverId);
      else pendingFull = true;
      clearTimeout(timer);
      timer = setTimeout(flush, DEBOUNCE_MS);
    };

    socket.on("merchant:ordersUpdate", handleOrderUpdate);
    return () => {
      clearTimeout(timer);
      socket.off("merchant:ordersUpdate", handleOrderUpdate);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    Object.values(framesRef.current).forEach((id) => cancelAnimationFrame(id));
    framesRef.current = {};
    lastPositionsRef.current = {};
    idsRef.current = new Set();
    setDrivers([]);
    setSelectedDriver(null);
    setFollowTargetId(null);
    fetchDriversRef.current();
    // eslint-disable-next-line
  }, [branchKey]);

  useEffect(() => {
    mountedRef.current = true;

    const reconcile = setInterval(() => fetchDriversRef.current(), RECONCILE_MS);
    const onReconnect = () => fetchDriversRef.current();
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchDriversRef.current();
    };

    socket.on("connect", onReconnect);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      mountedRef.current = false;
      clearInterval(reconcile);
      socket.off("connect", onReconnect);
      document.removeEventListener("visibilitychange", onVisible);
      Object.values(framesRef.current).forEach((id) =>
        cancelAnimationFrame(id)
      );
      framesRef.current = {};
    };
  }, []);

  useEffect(() => {
    const selected = selectedRef.current;
    if (!selected) return;
    const match = drivers.find((d) => d.driverId === selected.driverId);
    if (!match) {
      setSelectedDriver(null);
      setFollowTargetId(null);
      return;
    }
    if (match !== selected) setSelectedDriver(match);
    // eslint-disable-next-line
  }, [drivers]);

  const updateRef = useRef(updateSelectedDriver);
  updateRef.current = updateSelectedDriver;
  const selectDriver = useCallback(
    (driverId, follow = false) => updateRef.current(driverId, follow),
    []
  );

  return { drivers, updateSelectedDriver: selectDriver, followTargetId };
};

export default useDrivers;